import { CSSProperties, KeyboardEvent, useEffect, useRef, useState } from 'react';

import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Check, EditOutlined } from '@mui/icons-material';

import { AutoResizeTextarea } from 'components/AutoResizeTextarea/AutoResizeTextarea';
import { MutatingDot } from 'common/MutatingDot';

import { COLORS } from 'components/AI/DeterministicFileNameManagement/constants';
import {
  splitFilename,
  joinFilename,
} from 'components/AI/DeterministicFileNameManagement/utils';

// Props are a discriminated union — each variant exposes only the callbacks
// and knobs that are meaningful for that mode. TS will reject passing
// `isAccepting` to the post-accept variant (and vice-versa).
export type EditableFileNameProps =
  | {
      variant: 'suggestion';
      value: string;
      /**
       * Called with the full filename to accept (always present — view-mode
       * Accept passes the canonical suggestion; edit-mode Accept passes the
       * committed buffer). The `wasEdited` flag tells the parent whether the
       * click came from edit mode (true) or view mode (false), so a failed
       * accept can preserve the user's typed buffer only when they actually
       * had one.
       */
      onAccept: (fullFilename: string, opts: { wasEdited: boolean }) => Promise<void> | void;
      isAccepting: boolean;
      /**
       * Optional full filename used to seed the edit buffer on mount. When
       * present, the component mounts in edit mode with `editBase` set to the
       * buffer's base part (extension stripped). Used by the parent to
       * preserve the user's typed text across the unmount/remount cycle that
       * happens when an optimistic Accept fails. Pass undefined on the normal
       * first mount.
       */
      initialEditBuffer?: string;
      /** One-shot callback fired on mount after the buffer is consumed, so
       * the parent can clear its stash and avoid resurrecting it on later
       * remounts (e.g., after a template save). */
      onEditBufferConsumed?: () => void;
      /** Pulse the Accept button during onboarding spotlight */
      onboardingSpotlight?: boolean;
    }
  | {
      variant: 'post-accept';
      value: string;
      onRename: (fullFilename: string) => Promise<void> | void;
      editTitle?: string;
      saveTitle?: string;
      showEdit?: boolean;
      /**
       * When true, the editor closes the moment the user commits — before
       * `onRename` resolves — so the view-mode filename (already optimistically
       * updated by the parent) becomes visible immediately. On rejection the
       * editor re-opens with the user's buffer restored. Default false.
       */
      optimisticClose?: boolean;
      /**
       * Signals that the parent has a rename/accept mutation in flight.
       * In view mode this swaps the edit pencil for a small amber dot so
       * the user knows the optimistic filename hasn't been confirmed yet.
       * Editing remains blocked (the existing `isSaving`/`disabled` guard
       * already covers this).
       */
      isMutating?: boolean;
    };

// --- hoisted style constants ------------------------------------------------

const textareaStyle: CSSProperties = {
  flex: 1,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontWeight: 500,
  fontSize: 'inherit',
  fontFamily: 'inherit',
  resize: 'none',
  padding: 0,
  margin: 0,
};

// Inline style rather than sx so the primitive renders a plain <span> — safe
// to nest inside a parent <Typography> (avoids Typography-in-Typography
// re-baselining the font size) and still works as a flex child.
const extensionStyle: CSSProperties = {
  color: COLORS.text.disabled,
  fontWeight: 500,
  userSelect: 'none',
  whiteSpace: 'pre',
};

// Suggestion-variant outlined Cancel/Edit button. Theme-level overrides
// force a teal border on contained buttons; we neutralise that and own the
// focus ring explicitly so accepting via keyboard shows a consistent halo.
const outlinedButtonSx = {
  flex: 1,
  textTransform: 'none' as const,
  fontWeight: 500,
  color: COLORS.text.primary,
  border: `2px solid ${COLORS.surface.hoverBorder}`,
  backgroundColor: '#fff',
  outline: 'none',
  '&:hover': {
    backgroundColor: COLORS.surface.hover,
    border: `2px solid ${COLORS.surface.hoverBorder}`,
  },
  '&:focus, &.Mui-focusVisible, &:focus-visible': {
    outline: 'none',
    boxShadow: '0 0 0 3px rgba(0, 0, 0, 0.08)',
  },
};

const containedButtonSx = {
  flex: 1,
  backgroundColor: COLORS.accept.filled,
  color: '#fff',
  textTransform: 'none' as const,
  fontWeight: 600,
  boxShadow: 'none',
  outline: 'none',
  border: '2px solid transparent',
  '&:hover': {
    backgroundColor: COLORS.accept.filledHover,
    boxShadow: 'none',
    border: '2px solid transparent',
  },
  '&:active': { boxShadow: 'none' },
  '&:focus, &.Mui-focusVisible, &:focus-visible': {
    outline: 'none',
    boxShadow: `0 0 0 3px ${COLORS.accept.focusRing}`,
  },
  '&:disabled': {
    backgroundColor: COLORS.accept.filled,
    color: '#fff',
    opacity: 0.5,
    border: '2px solid transparent',
  },
};

const suggestionInputBoxSx = {
  display: 'flex',
  alignItems: 'last baseline',
  border: `1px solid ${COLORS.suggestion.inputBorder}`,
  borderRadius: '4px',
  background: '#fff',
  padding: '10px 12px',
};

const suggestionViewBoxSx = {
  padding: '10px 12px',
  borderRadius: '4px',
  backgroundColor: '#fff',
  border: `1px solid ${COLORS.suggestion.inputBorder}`,
  cursor: 'pointer',
  transition: 'border-color 0.15s ease',
  '&:hover': { borderColor: COLORS.suggestion.hoverAccent },
};

const postAcceptInputBoxSx = {
  flex: 1,
  display: 'flex',
  alignItems: 'flex-start',
  border: `1px solid ${COLORS.suggestion.inputBorderStrong}`,
  borderRadius: '4px',
  background: COLORS.suggestion.inputBg,
  padding: '4px 8px',
};

const postAcceptCheckButtonSx = {
  alignSelf: 'flex-end',
  border: `1px solid ${COLORS.accept.border}`,
  borderRadius: '4px',
  color: COLORS.accept.color,
  p: '4px',
  '&:hover': { backgroundColor: COLORS.accept.bgHover },
  '&:disabled': { opacity: 0.5, cursor: 'default' },
};

const postAcceptEditIconSx = {
  ml: 0.9375,
  p: '4px',
  verticalAlign: 'middle',
  '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
  '& > *:first-of-type': { fontSize: '14px' },
  '& > *:last-child': { fontSize: '14px' },
};

// --- small primitives -------------------------------------------------------

interface FilenameExtensionProps {
  ext: string;
  alignSelf?: 'baseline' | 'flex-end';
}

// Dimmed, non-selectable file-extension suffix. Rendered as a plain <span>
// so it works uniformly in two contexts: as a flex child (with optional
// alignSelf) and inline inside a parent Typography block.
const FilenameExtension = ({ ext, alignSelf }: FilenameExtensionProps) => {
  if (!ext) return null;
  const style = alignSelf ? { ...extensionStyle, alignSelf } : extensionStyle;
  return (
    <span aria-label={`extension ${ext}`} style={style}>
      {ext}
    </span>
  );
};

// --- main component ---------------------------------------------------------

export const EditableFileName = (props: EditableFileNameProps) => {
  const { base, ext } = splitFilename(props.value);

  // Restoration path: if the parent passed a buffer to seed the edit state
  // (suggestion variant only — used after an accept-failure remount), strip
  // the extension and use the base as the initial editBase, and mount in
  // edit mode. `useState`'s lazy initializer runs once on the first render,
  // so subsequent renders where initialEditBuffer is undefined don't reset
  // state — the buffer "ownership" transfers cleanly to local state after
  // mount.
  const initialBufferBase = (() => {
    if (props.variant !== 'suggestion' || !props.initialEditBuffer) return undefined;
    return splitFilename(props.initialEditBuffer).base;
  })();
  const [isEditing, setIsEditing] = useState(initialBufferBase !== undefined);
  const [editBase, setEditBase] = useState(initialBufferBase ?? base);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Suppresses the React 17 "set state on unmounted component" warning when
  // the parent remounts us mid-save (e.g., user opens a different document
  // while a rename is in flight). Not a correctness guard — just quiets dev.
  const mountedRef = useRef(true);

  const isSuggestion = props.variant === 'suggestion';
  const isAccepting = isSuggestion ? props.isAccepting : false;
  const editTitle = !isSuggestion ? props.editTitle ?? 'Edit file name' : 'Edit file name';
  const saveTitle = !isSuggestion ? props.saveTitle ?? 'Save new name' : 'Save new name';
  const showEdit = !isSuggestion ? props.showEdit ?? true : true;
  const optimisticClose = !isSuggestion ? props.optimisticClose ?? false : false;
  const isMutating = !isSuggestion ? props.isMutating ?? false : false;
  // While the parent mutation is in flight, treat the input as saving-equivalent
  // so existing click-to-edit and submit paths stay blocked. Without this an
  // Accept-then-immediately-click-pencil race could open the editor against
  // the not-yet-committed name.
  const disabled = isSaving || isAccepting || isMutating;
  // While editing, block the commit path when the buffer is empty or
  // whitespace-only. commit() also guards this, but disabling the button
  // communicates the blocked state rather than swallowing a click.
  const emptyEdit = isEditing && !editBase.trim();
  // In suggestion variant the primary button always means "Accept" — whether
  // the user is viewing or has edited. In post-accept variant it means "Save".
  const commitTitle = isSuggestion ? 'Accept suggested name' : saveTitle;
  const onboardingSpotlight =
    isSuggestion && props.onboardingSpotlight === true;

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  // One-shot consume of initialEditBuffer. The buffer has now been read into
  // local state via useState's initializer; tell the parent to clear its
  // stash so a later remount (e.g., after a template save flipping the view
  // back to suggestion) doesn't resurrect the old text. Empty deps because
  // we strictly want this on mount only — the callback identity is captured
  // at mount time which is fine since we only ever call it once.
  useEffect(() => {
    if (props.variant === 'suggestion' && props.initialEditBuffer !== undefined) {
      props.onEditBufferConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Keep edit buffer in sync with the underlying value when it changes
    // (e.g., suggestion refetch after template save).
    if (!isEditing) setEditBase(base);
  }, [base, isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    // Programmatic focus places the caret at position 0 by default. Users
    // entering edit mode via click-on-suggestion expect to append/tweak, so
    // drop the caret at the end of the buffer.
    const end = el.value.length;
    try { el.setSelectionRange(end, end); } catch { /* defensive */ }
  }, [isEditing]);

  const startEditing = () => {
    // Don't let users open the editor while a save/accept is in flight. Both
    // the suggestion-card body (role=button) and the post-accept edit icon
    // route through here, so this single guard covers every entry path.
    if (disabled) return;
    setEditBase(base);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setEditBase(base);
    setIsEditing(false);
  };

  const runSubmit = async (name: string, { closeEditor }: { closeEditor: boolean }) => {
    if (disabled) return;
    // Snapshot the user's typed buffer so we can restore it on rejection. The
    // parent's optimistic state update flips props.value, which (via the
    // !isEditing sync effect below) would otherwise overwrite editBase to the
    // newly-staged base before we get a chance to re-open the editor.
    const bufferAtSubmit = editBase;
    const shouldOptimisticClose = !isSuggestion && optimisticClose && closeEditor;
    setIsSaving(true);
    if (shouldOptimisticClose && mountedRef.current) setIsEditing(false);
    try {
      // Suggestion-variant: tell the parent whether the click came from edit
      // mode (closeEditor=true via commit) or view mode (closeEditor=false
      // via acceptAsIs) so an accept-failure parent can stash the buffer only
      // when there's actually a typed buffer to preserve.
      if (props.variant === 'suggestion') {
        await props.onAccept(name, { wasEdited: closeEditor });
      } else {
        await props.onRename(name);
      }
      if (!shouldOptimisticClose && closeEditor && mountedRef.current) setIsEditing(false);
    } catch (err) {
      if (shouldOptimisticClose && mountedRef.current) {
        setEditBase(bufferAtSubmit);
        setIsEditing(true);
      }
      // Parent handlers already toast the error. Swallow here so the rejection
      // doesn't escape to React as unhandled — and critically, leave isEditing
      // alone so the user's buffer remains for retry. Log a small sanitized
      // breadcrumb so a caller that forgets to toast still leaves a trace,
      // but never the raw error: Axios errors carry request config including
      // Authorization headers.
      // eslint-disable-next-line no-console
      console.error('[EditableFileName] submit failed', {
        name: err instanceof Error ? err.name : 'Unknown',
        message: err instanceof Error ? err.message : String(err),
        status: (err as { response?: { status?: number } })?.response?.status,
      });
    } finally {
      if (mountedRef.current) setIsSaving(false);
    }
  };

  // Accept without editing: submit the current canonical value as-is.
  const acceptAsIs = () => runSubmit(props.value, { closeEditor: false });

  const commit = () => {
    const trimmed = editBase.trim();
    if (!trimmed) return;
    const full = joinFilename({ base: trimmed, ext });
    // In the suggestion variant, accepting in edit mode must always forward
    // the name to the parent — even if the user didn't edit — otherwise the
    // accept signal is lost and they have to click Accept again in view mode.
    // In post-accept variant, an unchanged value is a no-op rename.
    if (full === props.value && !isSuggestion) {
      setIsEditing(false);
      return;
    }
    runSubmit(full, { closeEditor: true });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // While a save is in flight (or a parent mutation is mid-await), block
    // both commit and cancel so the user can't change or discard the buffer
    // out from under the request. readOnly on the textarea takes care of
    // direct typing; this guards Enter/Escape.
    if (disabled) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  // MUI buttons: triple-defense against focus jitter —
  //   - disableRipple      → avoids stray ripple animations that get trapped.
  //   - disableFocusRipple  → stops the ring from lingering after mousedown.
  //   - onMouseDown preventDefault → keeps the textarea's focus on click
  //     (so pressing Enter after editing doesn't blur-then-lose the commit).
  // Removing any one reintroduces a flicker we've already tracked down.

  if (isSuggestion) {
    // Unified structure across view and edit modes so the button DOM nodes
    // stay stable (only props toggle). Avoids React reconciliation flicker
    // on the buttons when isEditing flips.
    return (
      <Stack gap={0.625}>
        {isEditing ? (
          <Box sx={suggestionInputBoxSx}>
            <AutoResizeTextarea
              ref={inputRef}
              value={editBase}
              onChange={(e) => setEditBase(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={cancelEditing}
              readOnly={disabled}
              minHeight={24}
              style={textareaStyle}
            />
            <FilenameExtension ext={ext} />
          </Box>
        ) : (
          <Box
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            onClick={startEditing}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                startEditing();
              }
            }}
            sx={suggestionViewBoxSx}
          >
            <Typography sx={{ wordBreak: 'break-word', fontWeight: 500 }}>
              {base}
              <FilenameExtension ext={ext} />
            </Typography>
          </Box>
        )}
        <Stack direction="row" gap={0.625}>
          {/*
            Product direction: view mode shows only Accept (focus on the
            primary action); Cancel appears in the same slot only while
            editing. Both buttons are always rendered so the 50/50 layout
            stays stable — Cancel just becomes invisible and inert in view
            mode. Entering edit mode happens via click on the suggestion
            body above.
          */}
          <Button
            variant="outlined"
            onClick={cancelEditing}
            onMouseDown={(e) => e.preventDefault()}
            disabled={disabled}
            title="Cancel editing"
            disableRipple
            disableFocusRipple
            aria-hidden={!isEditing}
            tabIndex={isEditing ? 0 : -1}
            sx={{
              ...outlinedButtonSx,
              visibility: isEditing ? 'visible' : 'hidden',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Check />}
            onClick={isEditing ? commit : acceptAsIs}
            onMouseDown={(e) => e.preventDefault()}
            disabled={disabled || emptyEdit}
            title={isEditing ? commitTitle : 'Accept suggested name'}
            data-onboarding-target="file-rename-accept"
            disableRipple
            disableFocusRipple
            sx={{
              ...containedButtonSx,
              ...(onboardingSpotlight
                ? {
                    animation: 'onboardingAcceptPulse 2s ease-in-out infinite',
                    '@keyframes onboardingAcceptPulse': {
                      '0%, 100%': {
                        boxShadow: `0 0 0 3px ${COLORS.accept.focusRing}`,
                      },
                      '50%': {
                        boxShadow: `0 0 0 8px rgba(76, 175, 80, 0.2)`,
                      },
                    },
                  }
                : {}),
            }}
          >
            Accept
          </Button>
        </Stack>
      </Stack>
    );
  }

  if (isEditing) {
    // post-accept variant, editing: legacy compact row with small check icon.
    return (
      <Stack direction="row" alignItems="flex-start" gap={0.625}>
        <Box sx={postAcceptInputBoxSx}>
          <AutoResizeTextarea
            ref={inputRef}
            value={editBase}
            onChange={(e) => setEditBase(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={cancelEditing}
            readOnly={disabled}
            minHeight={24}
            style={textareaStyle}
          />
          <FilenameExtension ext={ext} alignSelf="flex-end" />
        </Box>
        <IconButton
          size="small"
          onClick={commit}
          onMouseDown={(e) => e.preventDefault()}
          disabled={disabled || emptyEdit}
          title={commitTitle}
          sx={postAcceptCheckButtonSx}
        >
          <Check sx={{ fontSize: '16px' }} />
        </IconButton>
      </Stack>
    );
  }

  // variant = 'post-accept', view: edit icon flows inline after the filename
  // so it stays next to the last character whether the name fits on one line
  // or wraps to several. While a mutation is in flight, the pencil is replaced
  // by an amber dot so the user can see that the optimistic filename hasn't
  // been confirmed yet.
  return (
    <Typography component="div" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
      {base}
      <FilenameExtension ext={ext} />
      {isMutating ? (
        <MutatingDot />
      ) : showEdit && (
        <Tooltip title={editTitle}>
          <IconButton
            aria-label={editTitle}
            onClick={startEditing}
            size="small"
            sx={postAcceptEditIconSx}
          >
            <EditOutlined sx={{ color: COLORS.icon.disabled }} />
          </IconButton>
        </Tooltip>
      )}
    </Typography>
  );
};
